import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { todayISO } from '../utils/helpers';
import * as api from '../services/apiService';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children, user, location: userLocation }) => {
  // ==================== CORE DATA ====================
  const location = userLocation || user?.location || '';
  const locationName = location === "krakow" ? "Kraków" : location === "grodek" ? "Gródek nad Dunajcem" : "Nowy Sącz";
  const locationIcon = location === "krakow" ? "🏰" : location === "grodek" ? "🌊" : "🏔️";
  // Admin = super-bypass (tryb developera). Manager/user = sprawdzane przez uprawnienia.
  // `isManager` zachowane jako alias dla kompatybilności wstecznej — true dla admin + każdy z permem 'manager' lub 'settings'.
  const isAdmin = user?.role === "admin";
  const hasManagerPanelAccess = isAdmin || (user?.permissions || []).includes('manager') || (user?.permissions || []).includes('settings');
  const isManager = hasManagerPanelAccess;

  // Main data collections
  const [waiters, setWaiters] = useState([]);
  const [entries, setEntries] = useState([]);
  const [closedDays, setClosedDays] = useState({});
  const [users, setUsers] = useState([]);
  const [billardEntries, setBillardEntries] = useState([]);
  const [clothingItems, setClothingItems] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const [clothingTypes, setClothingTypes] = useState([]);
  const [glassTypes, setGlassTypes] = useState([]);
  const [archivedBackups, setArchivedBackups] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // ==================== UI STATE ====================
  const [activeTab, setActiveTab] = useState("home");
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showInactive, setShowInactive] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Global refresh trigger — increments on each manual refresh; tabs subscribe via useEffect deps
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerGlobalRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  
  // Soft delete toggles
  const [showDeletedWaiters, setShowDeletedWaiters] = useState(false);
  const [showDeletedEntries, setShowDeletedEntries] = useState(false);
  const [showDeletedBillard, setShowDeletedBillard] = useState(false);
  const [showDeletedClothing, setShowDeletedClothing] = useState(false);

  // Expandable sections
  const [expandUsers, setExpandUsers] = useState(false);
  const [expandSystemLogs, setExpandSystemLogs] = useState(false);
  const [expandJobPositions, setExpandJobPositions] = useState(false);
  const [expandClothingTypes, setExpandClothingTypes] = useState(false);
  const [expandGlassTypes, setExpandGlassTypes] = useState(false);

  // ==================== FORM STATES ====================
  // Tips form
  const [tipDate, setTipDate] = useState(todayISO());
  const [selectedWaiter, setSelectedWaiter] = useState("");
  const [tipsAmount, setTipsAmount] = useState("");
  const [notes, setNotes] = useState("");

  // Waiter form
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [branch, setBranch] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [waiterStatus, setWaiterStatus] = useState("aktywny");
  const [workerCategory, setWorkerCategory] = useState("kelnerzy");

  // Reports
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportType, setReportType] = useState("tips");

  // Export
  const [exportFilter, setExportFilter] = useState("90");

  // Settings - Users
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [editingUserId, setEditingUserId] = useState(null);
  const [editPassword, setEditPassword] = useState("");
  const [editingPermissionsUserId, setEditingPermissionsUserId] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);

  // Settings - Job Positions & Clothing Types
  const [newJobPosition, setNewJobPosition] = useState("");
  const [editingJobPositionId, setEditingJobPositionId] = useState(null);
  const [editJobPositionName, setEditJobPositionName] = useState("");
  
  const [newClothingType, setNewClothingType] = useState("");
  const [editingClothingTypeId, setEditingClothingTypeId] = useState(null);
  const [editClothingTypeName, setEditClothingTypeName] = useState("");

  // Settings - Glass Types
  const [newGlassType, setNewGlassType] = useState("");
  const [editingGlassTypeId, setEditingGlassTypeId] = useState(null);
  const [editGlassTypeName, setEditGlassTypeName] = useState("");

  // Settings - Cleanup
  const [daysToKeep, setDaysToKeep] = useState(30);

  // Backup
  const [showBackupReminder, setShowBackupReminder] = useState(false);
  const [lastBackupDate, setLastBackupDate] = useState(null);

  // Refs
  const restoreFileRef = useRef(null);

  // ==================== CONSTANTS ====================
  const categoryDisplayNames = {
    'kelnerzy': 'Kelnerzy',
    'kierownik': 'Kierownicy',
    'kierowcy': 'Kierowcy',
    'barmani': 'Barmani'
  };

  const getCategoryDisplayName = (category) => {
    return categoryDisplayNames[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const hasPermission = (tabId) => {
    // ADMIN = super-bypass (tryb developera) — ma dostęp do wszystkiego.
    if (isAdmin) return true;
    return user?.permissions && user.permissions.includes(tabId);
  };

  // ==================== DATA FETCHING ====================
  const fetchGlassTypes = useCallback(async () => {
    try {
      const glassTypesRes = await api.getGlassTypes(location);
      setGlassTypes(glassTypesRes);
    } catch (error) {
      console.error('Error fetching glass types:', error);
    }
  }, [location]);

  const fetchBackupData = useCallback(async () => {
    try {
      const [backupsRes, lastBackupRes] = await Promise.all([
        api.getBackupList(location),
        api.getLastBackupInfo(location)
      ]);
      setArchivedBackups(backupsRes);
      setLastBackupDate(lastBackupRes.lastBackup);
    } catch (error) {
      console.error('Error fetching backup data:', error);
    }
  }, [location]);

  const fetchAuditLog = useCallback(async () => {
    try {
      const logs = await api.getAuditLog(location);
      setAuditLogs(logs);
    } catch (error) {
      console.error('Error fetching audit log:', error);
    }
  }, [location]);

  // ==================== CONTEXT VALUE ====================
  const value = {
    // User & Location
    user,
    location,
    locationName,
    locationIcon,
    isManager,
    isAdmin,
    hasPermission,
    getCategoryDisplayName,
    categoryDisplayNames,

    // Core Data
    waiters,
    setWaiters,
    entries,
    setEntries,
    closedDays,
    setClosedDays,
    users,
    setUsers,
    billardEntries,
    setBillardEntries,
    clothingItems,
    setClothingItems,
    jobPositions,
    setJobPositions,
    clothingTypes,
    setClothingTypes,
    glassTypes,
    setGlassTypes,
    archivedBackups,
    setArchivedBackups,
    auditLogs,
    setAuditLogs,

    // Global refresh trigger
    refreshKey,
    triggerGlobalRefresh,

    // UI State
    activeTab,
    setActiveTab,
    currentDateTime,
    setCurrentDateTime,
    showInactive,
    setShowInactive,
    expandedCategories,
    setExpandedCategories,
    showDeletedWaiters,
    setShowDeletedWaiters,
    showDeletedEntries,
    setShowDeletedEntries,
    showDeletedBillard,
    setShowDeletedBillard,
    showDeletedClothing,
    setShowDeletedClothing,
    expandUsers,
    setExpandUsers,
    expandSystemLogs,
    setExpandSystemLogs,
    expandJobPositions,
    setExpandJobPositions,
    expandClothingTypes,
    setExpandClothingTypes,
    expandGlassTypes,
    setExpandGlassTypes,

    // Form States - Tips
    tipDate,
    setTipDate,
    selectedWaiter,
    setSelectedWaiter,
    tipsAmount,
    setTipsAmount,
    notes,
    setNotes,

    // Form States - Waiter
    firstName,
    setFirstName,
    lastName,
    setLastName,
    branch,
    setBranch,
    birthDate,
    setBirthDate,
    waiterStatus,
    setWaiterStatus,
    workerCategory,
    setWorkerCategory,

    // Form States - Reports
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    reportType,
    setReportType,

    // Form States - Export
    exportFilter,
    setExportFilter,

    // Form States - Settings (Users)
    newUsername,
    setNewUsername,
    newPassword,
    setNewPassword,
    newRole,
    setNewRole,
    editingUserId,
    setEditingUserId,
    editPassword,
    setEditPassword,
    editingPermissionsUserId,
    setEditingPermissionsUserId,
    editingPermissions,
    setEditingPermissions,

    // Form States - Settings (Job Positions)
    newJobPosition,
    setNewJobPosition,
    editingJobPositionId,
    setEditingJobPositionId,
    editJobPositionName,
    setEditJobPositionName,

    // Form States - Settings (Clothing Types)
    newClothingType,
    setNewClothingType,
    editingClothingTypeId,
    setEditingClothingTypeId,
    editClothingTypeName,
    setEditClothingTypeName,

    // Form States - Settings (Glass Types)
    newGlassType,
    setNewGlassType,
    editingGlassTypeId,
    setEditingGlassTypeId,
    editGlassTypeName,
    setEditGlassTypeName,

    // Form States - Settings (Cleanup)
    daysToKeep,
    setDaysToKeep,

    // Backup
    showBackupReminder,
    setShowBackupReminder,
    lastBackupDate,
    setLastBackupDate,

    // Refs
    restoreFileRef,

    // Actions
    fetchGlassTypes,
    fetchBackupData,
    fetchAuditLog,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
